import { Except } from '@common/types/common.type';
import Icon from '@ui/components/atoms/icon/icon.atom';
import Input, { InputProps } from '@ui/components/molecules/input/input.molecule';
import withFormController from '@ui/hocs/with-form-controller/with-form-controller.hoc';
import useControlState from '@util/hooks/use-control-state/use-control-state.hook';
import useTimeout from '@util/hooks/use-timeout/use-timeout.hook';
import { ChangeEventHandler, memo, useCallback } from 'react';
import { FieldValues } from 'react-hook-form';

// #region Types
export type SearchProps<FieldType extends FieldValues> = Except<
  InputProps<FieldType>,
  'onChange'
> & {
  debounce?: boolean | number;
  onChange?: (text: string) => void;
  onSearchChange?: (text: string) => void;
};
// #endregion

function BaseSearch<FieldType extends FieldValues>({
  debounce,
  onChange,
  onSearchChange = () => {},
  trailingElement,
  value: passedValue,
  ...rest
}: SearchProps<FieldType>) {
  // #region States
  const [search, setSearch] = useControlState({
    controller: onChange,
    dependencies: [passedValue],
    initial: '',
  });
  // #endregion

  // #region Handlers
  const handleOnChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => setSearch(event.target.value),
    [setSearch],
  );
  // #endregion

  // #region Effects
  useTimeout(onSearchChange, debounce === true ? 200 : debounce || 0, search);
  // #endregion

  return (
    <Input<FieldType>
      {...rest}
      onChange={handleOnChange}
      trailingElement={trailingElement ?? <Icon.Search className="w-5 h-5" />}
      type="text"
      value={search as InputProps<FieldType>['value']}
    />
  );
}
BaseSearch.displayName = 'Search';

const Search = memo(withFormController(BaseSearch)) as typeof BaseSearch;

export default Search as typeof BaseSearch;
